import React, { useMemo, useState } from 'react';
import { intentDisplay, isOffNominal } from '../../utils/intentState';

/**
 * DronesPanel — the left overlay stack from the Stitch design:
 *  1) glass panel listing ACTIVE (connected) and INACTIVE drones
 *  2) detail card for the selected drone (battery / altitude / lat / lng)
 * Live data comes from the telemetry websocket (dronesTelemetry).
 */

const label10 = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.14em' };

function Icon({ name, size = 20, color, fill = false, style = {} }) {
    return (
        <span
            className="material-symbols-outlined"
            style={{
                fontSize: size,
                color,
                fontVariationSettings: fill ? "'FILL' 1" : "'FILL' 0",
                ...style,
            }}
        >
            {name}
        </span>
    );
}

function DronesPanel({ drones = [], dronesTelemetry = {}, selectedDroneId = null, onDroneSelect = null }) {
    const [activeOpen, setActiveOpen] = useState(true);
    const active = useMemo(() => {
        const act = [];

        drones.forEach((d) => {
            const tel = dronesTelemetry[d.id] || d.telemetry;
            const mode = String(tel?.mode || '').trim().toUpperCase();
            const isLive = !!tel?.connected && !tel?.stale && !!mode && mode !== 'N/A' && mode !== 'DISCONNECTED';
            if (isLive) {
                act.push({ ...d, telemetry: tel, readonly: false });
            }
        });

        return act;
    }, [drones, dronesTelemetry]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, pointerEvents: 'auto', maxHeight: '100%' }}>
            {/* ===== Drones list panel ===== */}
            <div className="glass-panel active-glow" style={{ width: 288, padding: 16, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div className="custom-scrollbar" style={{ overflowY: 'auto', paddingRight: 4, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Active */}
                    <div>
                        <button
                            type="button"
                            onClick={() => setActiveOpen((o) => !o)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'transparent', border: 'none', padding: 0, margin: '0 0 8px 0', cursor: 'pointer' }}
                        >
                            <span className="stitch-label" style={{ ...label10, color: 'var(--stitch-outline)' }}>
                                Active Drones{active.length ? ` · ${active.length}` : ''}
                            </span>
                            <Icon name="expand_more" size={18} color="var(--stitch-outline)" style={{ transform: activeOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }} />
                        </button>
                        {activeOpen && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {active.length === 0 && (
                                <div className="stitch-label" style={{ fontSize: 11, color: 'var(--textMuted)', padding: '8px 4px' }}>
                                    NO ACTIVE DRONES
                                </div>
                            )}
                            {active.map((d) => {
                                const tel = dronesTelemetry[d.id] || d.telemetry || {};
                                const isSel = d.id === selectedDroneId;
                                const battery = Number.isFinite(parseFloat(tel.battery_remaining))
                                    ? `${Math.round(parseFloat(tel.battery_remaining))}%`
                                    : '—';
                                // Only off-nominal claims get a badge. Tagging every
                                // normal flight "ON PLAN" would make the row noisy and
                                // teach operators to stop reading the badge at all.
                                const intent = intentDisplay(tel.intentState);
                                const offNominal = isOffNominal(tel.intentState);
                                return (
                                    <div
                                        key={d.id}
                                        onClick={() => { if (!d.readonly && onDroneSelect) onDroneSelect(d); }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: 8,
                                            borderRadius: 6,
                                            cursor: d.readonly ? 'default' : 'pointer',
                                            background: isSel ? 'rgba(0, 218, 243, 0.1)' : 'rgba(52, 53, 54, 0.2)',
                                            borderLeft: isSel ? '2px solid var(--accent)' : '2px solid transparent',
                                            transition: 'background 0.2s ease',
                                        }}
                                    >
                                        <Icon name="flight" size={20} color={isSel ? 'var(--accent)' : 'var(--textSecondary)'} style={{ marginRight: 12 }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p className="stitch-label" style={{ fontSize: 12, color: 'var(--textPrimary)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {(d.name || d.id).toUpperCase()}
                                            </p>
                                            <p style={{ fontSize: 10, color: 'rgba(0, 218, 243, 0.7)', margin: 0, fontFamily: 'var(--font-label)' }}>
                                                {d.readonly ? 'SHARED UNIT' : `MODE: ${(tel.mode || 'N/A').toUpperCase()}`}
                                            </p>
                                        </div>
                                        {offNominal && (
                                            <span
                                                className="stitch-label"
                                                title={`Airspace: ${intent.label}`}
                                                style={{
                                                    fontSize: 9,
                                                    letterSpacing: '0.1em',
                                                    color: intent.color,
                                                    border: `1px solid ${intent.color}`,
                                                    borderRadius: 3,
                                                    padding: '1px 4px',
                                                    marginRight: 6,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {intent.short}
                                            </span>
                                        )}
                                        <span className="stitch-label" style={{ fontSize: 11, color: 'var(--accent)' }}>{battery}</span>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DronesPanel;
